import { Text, ReactTable, Box } from '@wegroup/design-system';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell } from 'react-table';
import { toEuro } from '../../../../../../sales-conversation/src/utils/stringUtils';
import { CompanyLoan } from '../../../../types/CompanyScanTypes';
import InfoPopover from '../common/InfoPopover';
import TableActionsMenu from '../common/TableActionsMenu';
import TableFiller from '../common/TableFiller';

interface Props {
  companyLoans: CompanyLoan[];
  onEdit: (companyLoan: CompanyLoan, index: number) => void;
  onRemove: (index: number) => void;
}

const CompanyLoanTable: React.FC<React.PropsWithChildren<Props>> = ({
  companyLoans,
  onEdit,
  onRemove,
}) => {
  const { t } = useTranslation();

  const renderTypeCell = (cell: Cell) => {
    const { type } = cell?.row?.original as CompanyLoan;

    return (
      <Text color="text.gray">{t(`FLOWS_COMPANY-SCAN_LOAN-ENUM_${type}`)}</Text>
    );
  };

  const renderDurationCell = (cell: Cell) => {
    const { duration } = cell?.row?.original as CompanyLoan;

    return <Text color="text.gray">{`${duration} ${t('MONTHS')}`}</Text>;
  };

  const renderAmountCell = (cell: Cell) => {
    const { amount } = cell?.row?.original as CompanyLoan;

    return <Text color="text.gray">{toEuro(amount)}</Text>;
  };

  const renderPurposeCell = (cell: Cell) => {
    const { purpose } = cell?.row?.original as CompanyLoan;

    if (!purpose) return '-';

    return (
      <InfoPopover>
        <Text>{purpose}</Text>
      </InfoPopover>
    );
  };

  const renderLenderDetailsCell = (cell: Cell) => {
    const { lender_details } = cell?.row?.original as CompanyLoan;

    if (!lender_details) return '-';

    return (
      <InfoPopover>
        <Text>{lender_details}</Text>
      </InfoPopover>
    );
  };

  const renderActionMenuCell = (cell: Cell) => {
    const companyLoan = cell?.row?.original as CompanyLoan;
    const index = cell?.row?.index;

    return (
      <TableActionsMenu
        onEdit={() => onEdit(companyLoan, index)}
        onRemove={() => onRemove(index)}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_TYPE'),
        accessor: 'type',
        Cell: renderTypeCell,
        isSticky: true,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_DURATION'),
        accessor: 'duration',
        Cell: renderDurationCell,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_AMOUNT'),
        accessor: 'amount',
        Cell: renderAmountCell,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_PURPOSE'),
        accessor: 'purpose',
        Cell: renderPurposeCell,
        disableSortBy: true,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_LENDER-DETAILS'),
        accessor: 'details',
        Cell: renderLenderDetailsCell,
        disableSortBy: true,
      },
      {
        Header: t('ACTIONS'),
        id: 'actions',
        Cell: renderActionMenuCell,
        disableSortBy: true,
      },
    ],
    [companyLoans],
  );

  return (
    <Box>
      <ReactTable
        data={companyLoans}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        columns={columns as any}
      />
      {companyLoans.length === 0 && (
        <TableFiller>
          <Text>{t('FLOWS_COMPANY-SCAN_COMPANY-LOAN-INPUT_EMPTY-TABLE')}</Text>
        </TableFiller>
      )}
    </Box>
  );
};

export default CompanyLoanTable;
